import { JSX, useEffect, useMemo, useReducer, useState } from 'react';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { countBy, filter, flatMap, isEmpty, isNil, keys, maxBy, minBy, toLower, toString, trim, uniq } from 'lodash';
import { CompanyFundSummary } from 'api';
import { RESET, SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { COLUMN_TYPES, Filter, SortKey, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { CustomCompactContainer, CustomContainer, CustomScalarSpreadsheet, Tab } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useLayoutContext } from 'context/LayoutContext';
import {
  FIRM_SUMMARY_CONTAINER_CLASSNAME,
  FIRM_SUMMARY_KEY,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
  FIRM_SUMMARY_TITLE,
  FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE,
} from 'pages/Firms/common/constants';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { FirmSummarySpreadsheet, useFirmSummarySheet } from 'pages/Firms/hooks/useFirmSummarySheet';
import { InformationMessage } from 'pages/Funds/components';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useGetFirmSummary } from 'services/hooks/firms';
import { generateColumnKey, getArrayValue, getObjectValue, isValidDate } from 'utilities';
import { FirmCompanySummary, FundByCompany, UseFirmSummaryTabParams } from './useFirmSummaryTab.types';

const getRowKey = (row: FundByCompany): string =>
  generateColumnKey({
    id: getObjectValue(row).company_name,
    name: getObjectValue(row).fund_name,
    prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  });

const parseToNumber = (value: unknown): number | null => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getComparableValue = (row: FundByCompany, colType: string): string | number | null => {
  const safeRow = getObjectValue(row);

  switch (colType) {
    case COLUMN_TYPES.COMPANY_NAME:
      return safeRow.company_name ?? '';
    case COLUMN_TYPES.FUND_NAME:
      return safeRow.fund_name ?? '';
    case COLUMN_TYPES.VALUATION_DATE:
      return safeRow.measurement_date ? new Date(safeRow.measurement_date).getTime() : null;
    case COLUMN_TYPES.INITIAL_INVESTMENT_DATE:
      return safeRow.initial_investment_date ? new Date(safeRow.initial_investment_date).getTime() : null;
    case COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE:
      return safeRow.most_recent_investment_date ? new Date(safeRow.most_recent_investment_date).getTime() : null;
    case COLUMN_TYPES.DILUTED_OWNERSHIP:
      return parseToNumber(safeRow.diluted_ownership);
    case COLUMN_TYPES.OUTSTANDING_OWNERSHIP:
      return parseToNumber(safeRow.outstanding_ownership);
    case COLUMN_TYPES.INVESTED_CAPITAL:
      return parseToNumber(safeRow.fund_cost_basis);
    case COLUMN_TYPES.REALIZED_VALUE:
      return parseToNumber(safeRow.realized_value);
    case COLUMN_TYPES.UNREALIZED_VALUE:
      return parseToNumber(safeRow.unrealized_value);
    case COLUMN_TYPES.TOTAL_VALUE:
      return parseToNumber(safeRow.total_value);
    case COLUMN_TYPES.CURRENT_FUND_VALUE:
      return parseToNumber(safeRow.current_fund_value ?? safeRow.unrealized_value);
    case COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE:
      return parseToNumber(safeRow.breakeven_exit_equity_value_max ?? safeRow.breakeven_exit_equity_value_min);
    case COLUMN_TYPES.EQUITY_VALUE:
      return parseToNumber(safeRow.equity_value);
    case COLUMN_TYPES.ENTERPRISE_VALUE:
      return parseToNumber(safeRow.enterprise_value);
    case COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE:
      return parseToNumber(safeRow.total_insight_preference);
    case COLUMN_TYPES.FUND_PREFERENCE:
      return parseToNumber(safeRow.fund_preference);
    case COLUMN_TYPES.SPECIFIC_PREFERENCE:
      return parseToNumber(safeRow.specific_preference);
    case COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE:
      return parseToNumber(safeRow.total_company_preference);
    case COLUMN_TYPES.GROSS_IRR:
      return parseToNumber(safeRow.gross_irr);
    case COLUMN_TYPES.MOIC: {
      const invested = parseToNumber(safeRow.fund_cost_basis);
      const total = parseToNumber(safeRow.total_value);
      if (!invested || invested === 0) {
        return 0;
      }
      return (total ?? 0) / invested;
    }
    case COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS:
      return parseToNumber(safeRow.percent_change_from_previous);
    case COLUMN_TYPES.IMPLIED_ARR_MULTIPLE:
      return parseToNumber(safeRow.implied_arr_multiple);
    case COLUMN_TYPES.DCF_DISCOUNT_RATE:
      return parseToNumber(safeRow.dcf_discount_rate);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1:
      return parseToNumber(safeRow.projected_revenue_year_1);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2:
      return parseToNumber(safeRow.projected_revenue_year_2);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3:
      return parseToNumber(safeRow.projected_revenue_year_3);
    case COLUMN_TYPES.ALLOCATION_METHOD:
      return safeRow.allocation_method ?? '';
    case COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD:
      return safeRow.enterprise_valuation_method ?? '';
    case COLUMN_TYPES.VALUATION_APPROACH:
      return safeRow.valuation_approach ?? '';
    case COLUMN_TYPES.VALUATION_STATUS:
      return safeRow.valuation_status ?? '';
    default:
      return safeRow[colType] ?? null;
  }
};

const matchesFilter = (row: FundByCompany, filter: Filter, exclusionSet: Set<string>): boolean => {
  const [colType, [op, rawValue]] = filter;
  if (exclusionSet.has(colType)) {
    return true;
  }

  const rowValue = getComparableValue(row, colType);
  if (rowValue === null || typeof rawValue === 'undefined' || rawValue === null) {
    return false;
  }

  const normalizedRowValue = rowValue;
  const normalizedFilterValue =
    typeof normalizedRowValue === 'number' ? Number(rawValue) : toString(rawValue);

  if (typeof normalizedRowValue === 'number' && Number.isNaN(normalizedFilterValue)) {
    return false;
  }

  switch (op) {
    case '=':
      return normalizedRowValue === normalizedFilterValue;
    case '!=':
      return normalizedRowValue !== normalizedFilterValue;
    case '>':
      return Number(normalizedRowValue) > Number(normalizedFilterValue);
    case '>=':
      return Number(normalizedRowValue) >= Number(normalizedFilterValue);
    case '<':
      return Number(normalizedRowValue) < Number(normalizedFilterValue);
    case '<=':
      return Number(normalizedRowValue) <= Number(normalizedFilterValue);
    case 'contains':
      return toString(normalizedRowValue).toLowerCase().includes(toString(normalizedFilterValue).toLowerCase());
    default:
      return false;
  }
};

const applyFiltersWithHierarchy = (
  rows: FundByCompany[],
  filters: Filter[],
  filterExclusions: string[]
): FundByCompany[] => {
  const exclusionSet = new Set(filterExclusions ?? []);
  const activeFilters = (filters ?? []).filter(
    ([col, [, value]]) => !exclusionSet.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
  );

  if (!activeFilters.length) {
    return rows;
  }

  const groups = new Map<string, { parent?: FundByCompany; children: FundByCompany[] }>();
  const groupOrder: string[] = [];
  const orphans: FundByCompany[] = [];

  rows.forEach(row => {
    const key = getRowKey(row);
    const parentKey = getObjectValue(row).parent_key;

    if (getObjectValue(row).is_parent) {
      if (!groups.has(key)) {
        groupOrder.push(key);
      }
      groups.set(key, { parent: row, children: getObjectValue(groups.get(key))?.children ?? [] });
    } else if (parentKey) {
      if (!groups.has(parentKey)) {
        groupOrder.push(parentKey);
      }
      const currentGroup = groups.get(parentKey) ?? { parent: undefined, children: [] };
      currentGroup.children.push(row);
      groups.set(parentKey, currentGroup);
    } else {
      orphans.push(row);
    }
  });

  const matchesAll = (row: FundByCompany) => activeFilters.every(filter => matchesFilter(row, filter, exclusionSet));
  const result: FundByCompany[] = [];

  groupOrder.forEach(key => {
    const group = groups.get(key);
    if (!group) {
      return;
    }
    const filteredChildren = group.children.filter(matchesAll);
    const includeParent = group.parent ? matchesAll(group.parent) || filteredChildren.length > 0 : false;

    if (includeParent && group.parent) {
      result.push(group.parent);
    } else if (!filteredChildren.length) {
      return;
    }

    result.push(...filteredChildren);
  });

  orphans.forEach(row => {
    if (matchesAll(row)) {
      result.push(row);
    }
  });

  return result;
};

const sortGroupedRows = (rows: FundByCompany[], sortKeys: SortKey[]): FundByCompany[] => {
  const activeSortKeys = (sortKeys ?? []).filter(([col]) => Boolean(col));

  if (!activeSortKeys.length) {
    return rows;
  }

  const comparator = (a: FundByCompany, b: FundByCompany) => {
    let result = 0;
    activeSortKeys.some(([col, direction]) => {
      const aVal = getComparableValue(a, col);
      const bVal = getComparableValue(b, col);

      if (aVal === bVal) {
        return false; // continue to next sort key
      }

      if (aVal === null || typeof aVal === 'undefined') {
        result = 1;
        return true; // break
      }
      if (bVal === null || typeof bVal === 'undefined') {
        result = -1;
        return true; // break
      }

      let diff = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        diff = aVal - bVal;
      } else {
        diff = toString(aVal).localeCompare(toString(bVal), undefined, { sensitivity: 'base' });
      }

      if (diff !== 0) {
        result = direction === 'asc' ? diff : -diff;
        return true; // break
      }
      return false;
    });
    return result;
  };

  const groups = new Map<string, { parent?: FundByCompany; children: FundByCompany[] }>();
  const groupOrder: string[] = [];
  const orphans: FundByCompany[] = [];

  rows.forEach(row => {
    const key = getRowKey(row);
    const parentKey = getObjectValue(row).parent_key;

    if (getObjectValue(row).is_parent) {
      if (!groups.has(key)) {
        groupOrder.push(key);
      }
      groups.set(key, { parent: row, children: getObjectValue(groups.get(key))?.children ?? [] });
    } else if (parentKey) {
      if (!groups.has(parentKey)) {
        groupOrder.push(parentKey);
      }
      const currentGroup = groups.get(parentKey) ?? { parent: undefined, children: [] };
      currentGroup.children.push(row);
      groups.set(parentKey, currentGroup);
    } else {
      orphans.push(row);
    }
  });

  const sortedGroups = groupOrder
    .map(key => ({ key, group: groups.get(key) }))
    .filter(item => item.group)
    .sort((a, b) => {
      const left = a?.group?.parent ?? a?.group?.children?.[0];
      const right = b?.group?.parent ?? b?.group?.children?.[0];

      if (!left || !right) {
        return 0;
      }

      return comparator(left, right);
    });

  const result: FundByCompany[] = [];

  sortedGroups.forEach(item => {
    const group = item?.group;
    if (!group) {
      return;
    }
    if (group.parent) {
      result.push(group.parent);
    }
    if (group.children.length) {
      result.push(...[...group.children].sort(comparator));
    }
  });

  if (orphans.length) {
    result.push(...[...orphans].sort(comparator));
  }

  return result;
};

const useFirmSummaryTab = (params?: UseFirmSummaryTabParams) => {
  const { spreadsheetDefinition } = params ?? {};
  const [storeValue] = useStore() as UseStoreValues;
  const { companyList: currentCompanies, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);

  // Handle measurement date filter, query param, and context synchronization
  const {
    hasNoMeasurementDates,
    isLoadingMeasurementDate,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
  } = useFirmMeasurementDateFilter();

  const hasBreakevenColumn = useMemo(
    () =>
      Object.values(spreadsheetDefinition?.columns ?? {}).includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [spreadsheetDefinition]
  );
  const breakevenMultipleValue = hasBreakevenColumn ? Number(spreadsheetDefinition?.breakevenMultiple) : NaN;
  const breakevenMultiple = Number.isFinite(breakevenMultipleValue) && breakevenMultipleValue !== 1
    ? breakevenMultipleValue
    : undefined;

  const {
    data: firmSummary,
    isFetched: isFetchedFirmSummary,
    isFetching: isFetchingFirmSummary,
    isLoading: isLoadingFirmSummary,
  } = useGetFirmSummary({
    firmId: currentFirmId,
    measurementDate:
      !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
        ? selectedMeasurementDate.date
        : undefined,
    breakevenMultiple,
    shouldQueryAutomatically: !isNil(currentFirmId) && !isLoadingMeasurementDate,
  });

  // Use backend-calculated Gross IRR (includes all cash flows from FundOwnershipDetails)
  const { all_funds: fundNames, companies, firm_gross_irr: firmGrossIRR, funds_gross_irr: fundsGrossIRR, companies_gross_irr: companiesGrossIRR } = getObjectValue(firmSummary);

  const [selectedFund, setSelectedFund] = useState<string>(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);

  const fundsAvailable = useMemo(() => [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...getArrayValue(fundNames)], [fundNames]);

  const isAllFundsSelected = useMemo<boolean>(
    () => selectedFund === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
    [selectedFund]
  );

  // The most common fund currency across all funds
  const fundsCurrency = useMemo<string>(
    () =>
      maxBy(
        keys(countBy(flatMap(companies, 'funds'), 'fund_currency')),
        company => countBy(flatMap(companies, 'funds'), 'fund_currency')[company]
      ) ?? FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
    [companies]
  );

  const groupBy: SummaryGroupBy = useMemo(
    () => spreadsheetDefinition?.groupBy ?? 'company',
    [spreadsheetDefinition]
  );

  const sortKeys = useMemo<SortKey[]>(() => spreadsheetDefinition?.sortKeys ?? [], [spreadsheetDefinition]);
  const filters = useMemo<Filter[]>(() => spreadsheetDefinition?.filters ?? [], [spreadsheetDefinition]);
  const filterExclusions = useMemo<string[]>(
    () => spreadsheetDefinition?.filterExclusions ?? [],
    [spreadsheetDefinition]
  );

  const fundsByCompany = useMemo<FundByCompany[]>(
    () => {
      if (!companies) {
        return [];
      }

      if (groupBy === 'fund') {
        const groupOrder: string[] = [];
        const groupedFunds = new Map<string, FundByCompany[]>();

        companies.forEach(company => {
          const { company_id: companyId, company_name: companyName, company_slug: companySlug, funds } = company;

          funds.forEach(fund => {
            const parentKey = generateColumnKey({
              id: fund.fund_name,
              name: fund.fund_name,
              prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
            });

            if (!groupedFunds.has(parentKey)) {
              groupOrder.push(parentKey);
              groupedFunds.set(parentKey, []);
            }

            groupedFunds.get(parentKey)?.push({
              ...company,
              ...fund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
              company_id: companyId,
              company_name: companyName,
              company_slug: companySlug,
              is_parent: false,
              parent_key: parentKey,
            } as FundByCompany);
          });
        });

        const result: FundByCompany[] = [];

        groupOrder.forEach(key => {
          const children = groupedFunds.get(key) ?? [];
          if (!children.length) {
            return;
          }

          const sampleFund = children[0];

          const investedTotal = children.reduce((acc, row) => acc + (parseToNumber(row.fund_cost_basis) ?? 0), 0);
          const realizedTotal = children.reduce((acc, row) => acc + (parseToNumber(row.realized_value) ?? 0), 0);
          const unrealizedTotal = children.reduce((acc, row) => acc + (parseToNumber(row.unrealized_value) ?? 0), 0);
          const totalValue = realizedTotal + unrealizedTotal;
          const currentFundValueTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.current_fund_value ?? row.unrealized_value) ?? 0),
            0
          );
          const equityValueTotal = children.reduce((acc, row) => acc + (parseToNumber(row.equity_value) ?? 0), 0);
          const enterpriseValueTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.enterprise_value) ?? 0),
            0
          );
          const totalInsightPreferenceTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.total_insight_preference) ?? 0),
            0
          );
          const fundPreferenceTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.fund_preference) ?? 0),
            0
          );
          const specificPreferenceTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.specific_preference) ?? 0),
            0
          );
          const totalCompanyPreferenceTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.total_company_preference) ?? 0),
            0
          );
          const breakevenExitEquityValueMinTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_min) ?? 0),
            0
          );
          const breakevenExitEquityValueMaxTotal = children.reduce(
            (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_max) ?? 0),
            0
          );
          const initialInvestmentDate = toString(
            minBy(children, 'initial_investment_date')?.initial_investment_date
          );
          const mostRecentInvestmentDate = toString(
            maxBy(children, 'most_recent_investment_date')?.most_recent_investment_date
          );
          const mostRecentMeasurementDate = toString(maxBy(children, 'measurement_date')?.measurement_date);

          const parentRow: FundByCompany = {
            ...sampleFund,
            company_id: 0,
            company_name: sampleFund.fund_name,
            company_slug: '',
            company_captable_currency: sampleFund.fund_currency,
            exchange_rate_to_fund_currency: '1',
            diluted_ownership: null,
            outstanding_ownership: null,
            fund_cost_basis: toString(investedTotal),
            funds_count: children.length,
            gross_irr: fundsGrossIRR ? fundsGrossIRR[sampleFund.fund_name] : null,
            initial_investment_date: initialInvestmentDate,
            is_parent: true,
            measurement_date: mostRecentMeasurementDate,
            most_recent_investment_date: mostRecentInvestmentDate,
            parent_gross_irr: fundsGrossIRR ? fundsGrossIRR[sampleFund.fund_name] : null,
            parent_key: null,
            parent_type: 'fund',
            realized_value: toString(realizedTotal),
            total_value: toString(totalValue),
            unrealized_value: toString(unrealizedTotal),
            current_fund_value: toString(currentFundValueTotal),
            equity_value: toString(equityValueTotal),
            enterprise_value: toString(enterpriseValueTotal),
            total_insight_preference: toString(totalInsightPreferenceTotal),
            fund_preference: toString(fundPreferenceTotal),
            specific_preference: toString(specificPreferenceTotal),
            total_company_preference: toString(totalCompanyPreferenceTotal),
            breakeven_exit_equity_value_min: toString(breakevenExitEquityValueMinTotal),
            breakeven_exit_equity_value_max: toString(breakevenExitEquityValueMaxTotal),
            allocation_method: '',
            enterprise_valuation_method: '',
            valuation_approach: '',
            dcf_discount_rate: null,
            valuation_status: '',
            percent_change_from_previous: null,
            implied_arr_multiple: null,
            projected_revenue_year_1: null,
            projected_revenue_year_2: null,
            projected_revenue_year_3: null,
          };

          result.push(parentRow, ...children);
        });

        return result;
      }

      return flatMap(companies, company => {
        const { company_id: companyId, company_name: companyName, funds } = company;

        const currentFund = funds[0];
        const currentCompany: FirmCompanySummary = { company_id: companyId, company_name: companyName };

        // Standalone
        if (funds.length === 1) {
          return [
            {
              ...currentCompany,
              ...currentFund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
            } as FundByCompany,
          ];
        }

        // All fund names for this company, separated by commas
        const fundName: CompanyFundSummary['fund_name'] = funds.map(fund => fund.fund_name).join(', ');
        // The latest most recent measurement date across all funds for this company
        const mostRecentValuationDate: CompanyFundSummary['measurement_date'] = toString(
          maxBy(funds, 'measurement_date')?.measurement_date
        );
        // The earliest initial investment date across all funds for this company
        const initialInvestmentDate: CompanyFundSummary['initial_investment_date'] = toString(
          minBy(funds, 'initial_investment_date')?.initial_investment_date
        );
        // The latest most recent investment date across all funds for this company
        const mostRecentInvestmentDate: CompanyFundSummary['most_recent_investment_date'] = toString(
          maxBy(funds, 'most_recent_investment_date')?.most_recent_investment_date
        );

        // The most common fund currency across all funds for this company
        const companyFundsCurrency = maxBy(
          keys(countBy(funds, 'fund_currency')),
          fund => countBy(funds, 'fund_currency')[fund]
        );

        const investedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.fund_cost_basis) ?? 0), 0);
        const realizedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.realized_value) ?? 0), 0);
        const unrealizedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.unrealized_value) ?? 0), 0);
        const totalValue = realizedTotal + unrealizedTotal;
        const currentFundValueTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.current_fund_value ?? row.unrealized_value) ?? 0),
          0
        );
        const totalInsightPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.total_insight_preference) ?? 0),
          0
        );
        const fundPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.fund_preference) ?? 0),
          0
        );
        const specificPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.specific_preference) ?? 0),
          0
        );
        const totalCompanyPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.total_company_preference) ?? 0),
          0
        );
        const breakevenExitEquityValueMinTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_min) ?? 0),
          0
        );
        const breakevenExitEquityValueMaxTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_max) ?? 0),
          0
        );
        const equityValueTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.equity_value) ?? 0), 0);
        const enterpriseValueTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.enterprise_value) ?? 0), 0);

        const companyFund: FundByCompany = {
          ...currentCompany,
          ...currentFund,
          diluted_ownership: null,
          outstanding_ownership: null,
          fund_cost_basis: toString(investedTotal),
          fund_name: fundName,
          funds_count: funds.length,
          initial_investment_date: initialInvestmentDate,
          is_parent: true,
          most_recent_investment_date: mostRecentInvestmentDate,
          parent_gross_irr: companiesGrossIRR ? companiesGrossIRR[companyId] : null,
          parent_key: null,
          parent_type: 'company',
          realized_value: toString(realizedTotal),
          total_value: toString(totalValue),
          unrealized_value: toString(unrealizedTotal),
          current_fund_value: toString(currentFundValueTotal),
          equity_value: toString(equityValueTotal),
          enterprise_value: toString(enterpriseValueTotal),
          total_insight_preference: toString(totalInsightPreferenceTotal),
          fund_preference: toString(fundPreferenceTotal),
          specific_preference: toString(specificPreferenceTotal),
          total_company_preference: toString(totalCompanyPreferenceTotal),
          breakeven_exit_equity_value_min: toString(breakevenExitEquityValueMinTotal),
          breakeven_exit_equity_value_max: toString(breakevenExitEquityValueMaxTotal),
          allocation_method: '',
          enterprise_valuation_method: '',
          valuation_approach: '',
          dcf_discount_rate: null,
          valuation_status: '',
          percent_change_from_previous: null,
          implied_arr_multiple: null,
          projected_revenue_year_1: null,
          projected_revenue_year_2: null,
          projected_revenue_year_3: null,
          fund_currency: companyFundsCurrency ?? fundsCurrency,
          measurement_date: mostRecentValuationDate,
          fund_id: 0,
          fund_slug: '',
          gross_irr: companiesGrossIRR ? companiesGrossIRR[companyId] : null,
        };

        const companyFundKey = generateColumnKey({
          id: companyFund.company_name,
          name: companyFund.fund_name,
          prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        });

        const companyFunds = funds.map(
          fund =>
            ({
              ...currentCompany,
              ...fund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
              parent_key: companyFundKey,
            }) as FundByCompany
        );

        return [companyFund, ...companyFunds] as FundByCompany[];
      });
    },
    [companies, companiesGrossIRR, fundsCurrency, fundsGrossIRR, groupBy]
  );

  const filteredFundsByCompany = useMemo<FundByCompany[]>(() => {
    if (isAllFundsSelected) {
      return fundsByCompany;
    }

    return filter(fundsByCompany, fund => toLower(trim(fund.fund_name)) === toLower(trim(selectedFund)));
  }, [fundsByCompany, isAllFundsSelected, selectedFund]);

  const filteredByDefinition = useMemo<FundByCompany[]>(
    () => applyFiltersWithHierarchy(filteredFundsByCompany, filters, filterExclusions),
    [filteredFundsByCompany, filters, filterExclusions]
  );

  const orderedFundsByCompany = useMemo<FundByCompany[]>(
    () => sortGroupedRows(filteredByDefinition, sortKeys),
    [filteredByDefinition, sortKeys]
  );

  const hasDraftFunds = useMemo<boolean>(
    () => orderedFundsByCompany.some(fund => !fund.is_published && !fund.is_final),
    [orderedFundsByCompany]
  );

  const hasNoCompanies = useMemo<boolean>(
    () => !isNil(currentCompanies) && isEmpty(currentCompanies),
    [currentCompanies]
  );

  const hasNoInvestments = useMemo<boolean>(
    () =>
      hasNoMeasurementDates ||
      (Boolean(isFetchedFirmSummary) && isNil(firmSummary) && isEmpty(orderedFundsByCompany)),
    [firmSummary, hasNoMeasurementDates, isFetchedFirmSummary, orderedFundsByCompany]
  );

  const isLoading = useMemo<boolean>(
    () => isLoadingFirmSummary || isLoadingMeasurementDate,
    [isLoadingFirmSummary, isLoadingMeasurementDate]
  );

  // Spreadsheet

  const rowGroupParentKeys = useMemo<string[]>(
    () =>
      uniq(
        orderedFundsByCompany
          .map(item => getObjectValue(item).parent_key)
          .filter(Boolean)
          .map(key => toString(key))
      ),
    [orderedFundsByCompany]
  );

  const rowGroupsInitialState = useMemo<RowsGroups>(
    () =>
      Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])),
    [isAllFundsSelected, rowGroupParentKeys]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  const { spreadsheet: firmSummarySpreadsheet } = useFirmSummarySheet({
    fundsAvailable,
    fundsByCompany: orderedFundsByCompany,
    groupBy,
    isAllFundsSelected,
    selectedFund,
    setSelectedFund,
    firmGrossIRR,
    fundsGrossIRR,
    companiesGrossIRR,
    spreadsheetDefinition,
  });

  const spreadsheets = useMemo<FirmSummarySpreadsheet[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: private 'yLength' property
      firmSummarySpreadsheet?.yLength ? [firmSummarySpreadsheet] : [],
    [firmSummarySpreadsheet]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: private 'shouldValidate' property
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const [format, formatDispatch] = useFormat({
    measurementDate: isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate,
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: fundsCurrency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const firmSummarySpreadsheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (firmSummarySpreadsheet) {
      return {
        ...firmSummarySpreadsheet,
        data: getArrayValue(firmSummarySpreadsheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: firmSummarySpreadsheet,
        workbook,
        rowGroups,
        setRowGroups,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [firmSummarySpreadsheet, format, formatDispatch, onChange, rowGroups, workbook]);

  const { isExportMode } = useLayoutContext();

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const firmSummaryTabContent = useMemo<JSX.Element | null>(() => {
    if (hasNoCompanies || hasNoInvestments) {
      return null;
    }

    return (
      <CustomContainer data-ref={PdfExportTargets.FIRM_SUMMARY.id}>
        <Grid className={FIRM_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
          {isLoading ? (
            // Loading grid skeleton while fetching data
            <GridSkeleton maxColumns={12} maxRows={4} />
          ) : (
            firmSummarySpreadsheetProps &&
            firmSummarySpreadsheet && (
              <WorkbookContext.Provider value={workbookContextValue}>
                <CustomCompactContainer>
                  <Grid
                    className={clsx(FIRM_SUMMARY_CONTAINER_CLASSNAME, { expanded: hasExpandedRowGroup })}
                    size={{ xs: 12 }}>
                    <CustomScalarSpreadsheet {...firmSummarySpreadsheetProps} isLoading={isFetchingFirmSummary} />
                  </Grid>

                  {!isExportMode && (
                    <InformationMessage
                      displayMessage={hasDraftFunds}
                      message={FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE}
                    />
                  )}
                </CustomCompactContainer>
              </WorkbookContext.Provider>
            )
          )}
        </Grid>
      </CustomContainer>
    );
  }, [
    firmSummarySpreadsheet,
    firmSummarySpreadsheetProps,
    hasDraftFunds,
    hasExpandedRowGroup,
    hasNoCompanies,
    hasNoInvestments,
    isFetchingFirmSummary,
    isLoading,
    workbookContextValue,
    isExportMode,
  ]);

  // Tab
  const firmSummaryTab = useMemo<Tab>(
    () =>
      ({
        id: FIRM_SUMMARY_KEY,
        label: FIRM_SUMMARY_TITLE,
        content: firmSummaryTabContent,
      }) as Tab,
    [firmSummaryTabContent]
  );

  // Reset selected fund when measurement date changes
  useEffect(() => {
    if (!isLoadingMeasurementDate) {
      setSelectedFund(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);
    }
  }, [isLoadingMeasurementDate, selectedMeasurementDate]);

  // Synchronize rowGroups state with dynamic parent keys
  useEffect(() => {
    if (!isEmpty(rowGroupsInitialState)) {
      setRowGroups({ payload: rowGroupsInitialState, type: RESET });
    }
  }, [rowGroupsInitialState]);

  return { firmSummaryTab, hasNoCompanies, hasNoInvestments };
};

export default useFirmSummaryTab;
